import { injectable } from 'inversify';
import { isEqual, sortBy } from 'lodash';

import {
  Enums,
  SharedModels,
  UIDtoFactory,
} from '@project44-models/iam-gateway';

import { UnknownPropertyException } from '../../../shared/core/application/errors';
import { UnifiedRolesMapper } from '../domain/mapper/UnifiedRoles.mapper';
import { UnknownUnifiedRoleException } from './errors/UnknownUnifiedRoleException';
import {
  TUnifiedRole,
  TUnifiedRoleCollectionDto,
  TUnifiedRolesMap,
} from './interfaces';

@injectable()
export class UnifiedRolesEditor {
  public composeResourceTenantAgnosticRolesMap(payload: {
    availableTenantAgnosticRolesCollection: TUnifiedRoleCollectionDto;
    assignedTenantAgnosticRolesCollection: TUnifiedRoleCollectionDto;
  }): TUnifiedRolesMap {
    const {
      assignedTenantAgnosticRolesCollection,
      availableTenantAgnosticRolesCollection,
    } = payload;

    const resourceTenantAgnosticRolesMap: TUnifiedRolesMap = new Map();

    for (const availableTenantAgnosticRole of availableTenantAgnosticRolesCollection.source.getRoles()) {
      resourceTenantAgnosticRolesMap.set(
        availableTenantAgnosticRole.id,
        undefined,
      );
    }

    for (const assignedTenantAgnosticRole of assignedTenantAgnosticRolesCollection.source.getRoles()) {
      resourceTenantAgnosticRolesMap.set(
        assignedTenantAgnosticRole.id,
        assignedTenantAgnosticRole,
      );
    }

    return new Map(resourceTenantAgnosticRolesMap);
  }

  public composeResourceTenantSpecificRolesMap(payload: {
    assignedTenantSpecificRoles: TUnifiedRoleCollectionDto;
    availableTenantSpecificRoles: TUnifiedRoleCollectionDto;
  }): TUnifiedRolesMap {
    const resourceTenantSpecificRolesMap =
      this.composeRolesMapFromRoleCollectionDto({
        unifiedRoleCollectionDto: payload.assignedTenantSpecificRoles,
      });

    const resourceTenantSpecificRoles = new Map();

    for (const unifiedRoleModel of Array.from(
      new Set([
        ...payload.assignedTenantSpecificRoles.source.getRoles(),
        ...payload.availableTenantSpecificRoles.source.getRoles(),
      ]),
    )) {
      resourceTenantSpecificRoles.set(
        unifiedRoleModel.id,
        resourceTenantSpecificRolesMap.get(unifiedRoleModel.id),
      );
    }

    return new Map(resourceTenantSpecificRoles);
  }

  public removeRoleFromRolesMap(payload: {
    targetUnifiedRolesMap: TUnifiedRolesMap;
    unifiedRoleIdToRemove: string;
  }): TUnifiedRolesMap {
    const { targetUnifiedRolesMap, unifiedRoleIdToRemove } = payload;

    if (!targetUnifiedRolesMap.has(payload.unifiedRoleIdToRemove)) {
      UnknownPropertyException.throw([payload.unifiedRoleIdToRemove]);
    }

    return new Map(targetUnifiedRolesMap.set(unifiedRoleIdToRemove, undefined));
  }

  public removeAllRolesFromRolesMap(payload: {
    targetUnifiedRolesMap: TUnifiedRolesMap;
  }): TUnifiedRolesMap {
    const { targetUnifiedRolesMap } = payload;

    const undefinedUnifiedRolesDictionary: Record<string, undefined> = {};

    for (const [unifiedRoleId] of targetUnifiedRolesMap) {
      undefinedUnifiedRolesDictionary[unifiedRoleId] = undefined;
    }

    return new Map(Object.entries(undefinedUnifiedRolesDictionary));
  }

  public addAllNonTenantAdminRelatedRolesToAssignedRoles(payload: {
    targetUnifiedRolesMap: TUnifiedRolesMap;
    assignedRolesCollection: TUnifiedRoleCollectionDto;
    availableRolesCollection: TUnifiedRoleCollectionDto;
  }): TUnifiedRolesMap {
    const assignedRoles = this.composeRolesMapFromRoleCollectionDto({
      unifiedRoleCollectionDto: payload.assignedRolesCollection,
    });

    const allAvailableRoles = this.composeRolesMapFromRoleCollectionDto({
      unifiedRoleCollectionDto: payload.availableRolesCollection,
    });

    const assignedUnifiedRolesMap: TUnifiedRolesMap = new Map(
      Array.from(payload.targetUnifiedRolesMap),
    );

    payload.targetUnifiedRolesMap.forEach((value, key) => {
      const maybeUnifiedRole: SharedModels.UnifiedRoleViewModel | undefined =
        assignedRoles.get(key) || allAvailableRoles.get(key);

      if (!this.isRoleTenantAdminRelatedRole(maybeUnifiedRole)) {
        assignedUnifiedRolesMap.set(key, maybeUnifiedRole);
      }
    });

    return new Map(assignedUnifiedRolesMap);
  }

  public removeAllNonTenantAdminRelatedRolesFromAssignedRoles(payload: {
    targetUnifiedRolesMap: TUnifiedRolesMap;
  }): TUnifiedRolesMap {
    const { targetUnifiedRolesMap } = payload;

    return new Map(
      Array.from(targetUnifiedRolesMap.entries()).map(([roleId, maybeRole]) => {
        return [
          roleId,
          this.isRoleTenantAdminRelatedRole(maybeRole) ? maybeRole : undefined,
        ];
      }),
    );
  }

  public readonly isRoleTenantAdminRelatedRole = (
    maybeTenantAdminRelatedRole?: SharedModels.UnifiedRoleViewModel,
  ) => {
    return (
      this.checkUnifiedRoleIsTenantAdminUnifiedRole(
        maybeTenantAdminRelatedRole,
      ) ||
      this.checkUnifiedRoleIsNMCLegacyTenantAdminUnifiedRole(
        maybeTenantAdminRelatedRole,
      ) ||
      this.checkUnifiedRoleIsNMCLegacyReadOnlyUnifiedRole(
        maybeTenantAdminRelatedRole,
      )
    );
  };

  public addRoleToRolesMap(payload: {
    targetUnifiedRolesMap: TUnifiedRolesMap;
    sourceUnifiedRolesCollection: TUnifiedRoleCollectionDto;
    unifiedRoleIdToAdd: string;
  }): TUnifiedRolesMap {
    const {
      targetUnifiedRolesMap,
      sourceUnifiedRolesCollection,
      unifiedRoleIdToAdd,
    } = payload;

    const availableRolesMap = this.composeRolesMapFromRoleCollectionDto({
      unifiedRoleCollectionDto: sourceUnifiedRolesCollection,
    });

    if (!targetUnifiedRolesMap.has(unifiedRoleIdToAdd)) {
      UnknownPropertyException.throw([payload.unifiedRoleIdToAdd]);
    }

    return new Map(
      targetUnifiedRolesMap.set(
        unifiedRoleIdToAdd,
        availableRolesMap.get(unifiedRoleIdToAdd),
      ),
    );
  }

  public addAllRolesToRolesMap(payload: {
    targetUnifiedRolesMap: TUnifiedRolesMap;
    assignedRolesCollection: TUnifiedRoleCollectionDto;
    availableRolesCollection: TUnifiedRoleCollectionDto;
  }): TUnifiedRolesMap {
    const assignedRolesMap = this.composeRolesMapFromRoleCollectionDto({
      unifiedRoleCollectionDto: payload.assignedRolesCollection,
    });

    const allAvailableRolesMap = this.composeRolesMapFromRoleCollectionDto({
      unifiedRoleCollectionDto: payload.availableRolesCollection,
    });

    const allUnifiedRolesMap: TUnifiedRolesMap = new Map();

    payload.targetUnifiedRolesMap.forEach((value, key) => {
      const maybeUnifiedRole: SharedModels.UnifiedRoleViewModel | undefined =
        assignedRolesMap.get(key) || allAvailableRolesMap.get(key);

      if (maybeUnifiedRole === undefined) {
        UnknownUnifiedRoleException.throw(maybeUnifiedRole);
      }

      allUnifiedRolesMap.set(key, maybeUnifiedRole);
    });

    return new Map(allUnifiedRolesMap);
  }

  public compareResourceUnifiedRolesState(payload: {
    resourceUnifiedRolesState: [TUnifiedRolesMap, TUnifiedRoleCollectionDto];
  }): boolean {
    const {
      resourceUnifiedRolesState: [unifiedRolesMap, unifiedRoleCollectionDto],
    } = payload;
    return isEqual(
      sortBy(Array.from(unifiedRolesMap.values()).filter(Boolean), ['id']),
      sortBy(unifiedRoleCollectionDto.source.getRoles(), ['id']),
    );
  }

  public composeRoleCollectionDtoFromRolesMap(payload: {
    unifiedRolesMap: TUnifiedRolesMap;
  }): TUnifiedRoleCollectionDto {
    const rolesCollection: SharedModels.UnifiedRoleCollectionViewModel['roles'] =
      [];

    payload.unifiedRolesMap.forEach((maybeTenantAgnosticRole) => {
      if (!!maybeTenantAgnosticRole) {
        rolesCollection.push(maybeTenantAgnosticRole);
      }
    });

    return UIDtoFactory.create(Enums.UiDtoFactoryEnum.UNIFIED_ROLE_COLLECTION, {
      roles: rolesCollection,
    });
  }

  public checkUnifiedRoleIsNMCLegacyTenantAdminUnifiedRole(
    unifiedRole?: SharedModels.UnifiedRoleViewModel,
  ): boolean {
    try {
      return !!UnifiedRolesMapper.assertNMCLegacyTenantAdminRole(unifiedRole);
    } catch (e) {
      return false;
    }
  }

  public readonly getAllTenantSpecificAdministrationRoles = (payload: {
    tenantSpecificRoles: TUnifiedRoleCollectionDto;
  }): {
    nmcLegacyTenantAdminRole: TUnifiedRole;
    tenantAdminRole: TUnifiedRole;
    nmcLegacyReadOnlyRole: TUnifiedRole;
  } => {
    const { roles } = payload.tenantSpecificRoles.toSerializedModel();

    const maybeNMCLegacyTenantAdminUnifiedRole = roles.find((item) => {
      return this.checkUnifiedRoleIsNMCLegacyTenantAdminUnifiedRole(item);
    });

    const maybeNMCLegacyReadOnlyRole = roles.find((item) => {
      return this.checkUnifiedRoleIsNMCLegacyReadOnlyUnifiedRole(item);
    });

    const maybeTenantAdminRole = roles.find((item) => {
      return this.checkUnifiedRoleIsTenantAdminUnifiedRole(item);
    });

    return {
      tenantAdminRole:
        UnifiedRolesMapper.assertTenantAdminRole(maybeTenantAdminRole),
      nmcLegacyTenantAdminRole:
        UnifiedRolesMapper.assertNMCLegacyTenantAdminRole(
          maybeNMCLegacyTenantAdminUnifiedRole,
        ),
      nmcLegacyReadOnlyRole: UnifiedRolesMapper.assertNMCLegacyReadOnlyRole(
        maybeNMCLegacyReadOnlyRole,
      ),
    };
  };
  public checkUnifiedRoleIsTenantAdminUnifiedRole(
    unifiedRole?: SharedModels.UnifiedRoleViewModel,
  ): boolean {
    try {
      return !!UnifiedRolesMapper.assertTenantAdminRole(unifiedRole);
    } catch (e) {
      return false;
    }
  }

  public checkUnifiedRoleIsNMCLegacyReadOnlyUnifiedRole(
    unifiedRole?: SharedModels.UnifiedRoleViewModel,
  ): boolean {
    try {
      return !!UnifiedRolesMapper.assertNMCLegacyReadOnlyRole(unifiedRole);
    } catch (e) {
      return false;
    }
  }

  private composeRolesMapFromRoleCollectionDto(payload: {
    unifiedRoleCollectionDto: TUnifiedRoleCollectionDto;
  }): TUnifiedRolesMap {
    const unifiedRolesMap: TUnifiedRolesMap = new Map();

    for (const unifiedRoleModel of payload.unifiedRoleCollectionDto.source.getRoles()) {
      unifiedRolesMap.set(unifiedRoleModel.id, unifiedRoleModel);
    }

    return new Map(unifiedRolesMap);
  }
}
